import { Decimal } from 'decimal.js';
import BigDecimal from 'js-big-decimal';
import { isNumber } from 'lodash';

export const MAX_INTEGER = 2147483647;

export const safeMultiply = (a: number, b: number, precision = 2): number => {
  // Use the money class to multiply to avoid floating point error
  const aBigDecimal = new BigDecimal(a);
  const bBigDecimal = new BigDecimal(b);
  return parseFloat(
    aBigDecimal.multiply(bBigDecimal).round(precision).getValue(),
  );
};

export const multipleMany = (numbers: number[], precision = 3): number => {
  // Use the money class to multiply to avoid floating point error
  // Need to keep precision here so we do not loss precision
  // due to rounding throughout the process
  // let total = chain(1);
  // numbers.forEach((number) => {
  //   total.multiply(number);
  // });
  // return round(total.done(), 2);
  let total = new BigDecimal(1);
  numbers.forEach((number) => {
    const numberBigDecimal = new BigDecimal(number);
    total = total.multiply(numberBigDecimal);
  });
  return parseFloat(total.round(precision).getValue());
};

/**
 * Decimals `safeDivide` keeps internally, before it rounds to the `precision` its
 * caller asked for. Governs every caller — money included — so it is deliberately
 * not the weight constant below: naming one scale for weight and then letting it
 * set the scale for `centsToDollars` would mean a weight change silently altering
 * money arithmetic.
 *
 * Change this only with all 180-plus call sites in mind.
 */
const DIVIDE_SCALE = 10;

/**
 * The precision to use at both ends of a divide-then-sum round trip — splitting a
 * package weight across its pieces and totalling it back up.
 *
 * Ten is a ceiling rather than a preference: it is capped by `DIVIDE_SCALE`, which
 * `safeDivide` applies before its `precision` argument, so asking for more changes
 * nothing. A non-terminating quotient therefore does not round-trip exactly — 100
 * across three pieces sums back to 99.9999999999. That residue is ~1e-10 and does
 * not compound; it is not a lossless round trip, and nothing should claim otherwise.
 *
 * Single source of truth so the two tiers cannot drift: the backend divides with
 * it when generating pieces for a transmitted package, and the order form sums
 * with it when deriving the package total back.
 */
export const WEIGHT_PRECISION = 10;

export const safeDivide = (a: number, b: number, precision = 2): number => {
  // Use the money class to divide to avoid floating point error
  if (b === 0) {
    return 0;
  }
  const aBigDecimal = new BigDecimal(a);
  const bBigDecimal = new BigDecimal(b);
  return parseFloat(
    aBigDecimal
      .divide(bBigDecimal, DIVIDE_SCALE)
      .round(precision, BigDecimal.RoundingModes.HALF_UP)
      .getValue(),
  );
};

export const centsToDollars = (cents: number | Decimal): number => {
  if (isNumber(cents)) {
    return safeDivide(cents, 100, 3);
  }

  return cents
    .dividedBy(100)
    .toDecimalPlaces(3, Decimal.ROUND_HALF_UP)
    .toNumber();
};

/**
 * Takes in a percentage value out of 100 and returns a decimal representation of the percentage.
 * @param percentage
 */
export const percentageToDecimal = (percentage: number): number => {
  return safeDivide(percentage, 100, 4);
};

/**
 * Takes in a decimal value out of 1 and returns a percentage out of 100.
 * @param decimal
 */
export const decimalToPercentage = (decimal: number): number => {
  return safeMultiply(decimal, 100);
};

export const safeAdd = (a: number, b: number): number => {
  // Use the money class to multiply to avoid floating point error
  const aBigDecimal = new BigDecimal(a);
  const bBigDecimal = new BigDecimal(b);
  return parseFloat(aBigDecimal.add(bBigDecimal).round(2).getValue());
};

export const addMany = (numbers: number[], precision = 3): number => {
  // Use the money class to multiply to avoid floating point error
  // Need to keep precision here so we do not loss precision
  // due to rounding throughout the process
  // let total = chain(1);
  // numbers.forEach((number) => {
  //   total.multiply(number);
  // });
  // return round(total.done(), 2);
  let total = new BigDecimal(0);
  numbers.forEach((number) => {
    const numberBigDecimal = new BigDecimal(number);
    total = total.add(numberBigDecimal);
  });
  return parseFloat(total.round(precision).getValue());
};

export const safeSubtract = (a: number, b: number): number => {
  // Use the money class to multiply to avoid floating point error
  const aBigDecimal = new BigDecimal(a);
  const bBigDecimal = new BigDecimal(b);
  return parseFloat(aBigDecimal.subtract(bBigDecimal).round(2).getValue());
};
